(ns machtfit.startpage.filterbar
  (:require [clojure.set :as set]
            [clojure.string :as str]
            [goog.functions :refer [debounce]]
            [goog.string :as gstring]
            [machtfit.api :as api]
            [machtfit.app.routes :as routes]
            [machtfit.app.tracking :as tracking]
            [machtfit.core :as machtfit]
            [machtfit.feature :as feature]
            [machtfit.human :refer [weekdays weekday]]
            [machtfit.startpage.events :as events]
            [machtfit.startpage.filter :as filter]
            [machtfit.startpage.location :as location :refer [locations-equal?]]
            [machtfit.startpage.statics :as statics]
            [machtfit.startpage.subs :as subs]
            [machtfit.translation :as translation :refer [t]]
            [machtfit.utils :as utils]
            [machtfit.widgets.switch :as switch]
            [re-frame.core :as rf]
            [reagent.core :as r]))

(defn bubble [filter-key value label on-close]
  (let [ph-ui-id
        (tracking/id "filter"
                     (str/join "-"
                               (remove nil?
                                       ["bubble"
                                        (name filter-key)
                                        (when (string? value)
                                          (tracking/coerce-key value))])))
        close-icon (str "url(" (machtfit/static "img/x.svg") ")")]
    [:span.filter-bubble-label {:data-ph-capture-attribute-ui_id ph-ui-id}
     label
     [:button
      {:aria-label (t {:de "Filter entfernen"
                       :en "Remove filter"})
       :style {:background-image close-icon}
       :on-click #(do
                    (rf/dispatch-sync [::events/set-filter-action {:type "bubble"
                                                                   :details {:filter-key filter-key
                                                                             :was_added false
                                                                             :value value}}])
                    (on-close))}]]))

(defn- single-bubble [filter-key translations]
  (when-let [value @(rf/subscribe [::subs/effective-filter filter-key])]
    (when-not (str/blank? value)
      [bubble
       filter-key
       value
       (t translations)
       #(rf/dispatch [::events/set-filter-false filter-key])])))

(defn- single-bubble--time [filter-key value-prefix default-filter]
  (let [value @(rf/subscribe [::subs/effective-filter filter-key])]
    (when-not (= value default-filter)
      [bubble
       filter-key
       value
       (str value-prefix " " value (t {:de " Uhr"
                                       :en ""}))
       #(rf/dispatch [::events/remove-filter filter-key])])))

(defn- multi-bubble [filter-key translations]
  (when-let [values (sort @(rf/subscribe [::subs/effective-filter filter-key]))]
    [:<>
     (doall
      (for [value values
            :let [label (if translations
                          (t (get translations value))
                          value)]]
        ^{:key (str filter-key value)}
        [bubble filter-key value label #(rf/dispatch [::events/remove-filter-element filter-key value])]))]))

(defn- category-bubbles []
  (when-let [filter-categories @(rf/subscribe [::subs/effective-filter :categories])]
    (let
     [categories (set @(rf/subscribe [::subs/non-empty-categories-in-current-area]))
      product-categories (set/intersection categories statics/product-categories)
      activity-categories (set/difference categories statics/product-categories)]
      [:<>
       (doall
        (for [category (sort activity-categories)
              :when (utils/in? filter-categories category)]
          ^{:key category}
          [bubble :categories category
           (t {:de category
               :en (statics/category-translations category)})
           #(rf/dispatch [::events/remove-filter-element :categories category])]))

       (doall
        (for [category (sort product-categories)
              :when (utils/in? filter-categories category)]
          ^{:key category}
          [bubble :categories category
           (t {:de category
               :en (statics/category-translations category)})
           #(rf/dispatch [::events/remove-filter-element :categories category])]))])))

(defn- area-bubble []
  (when-let [area @(rf/subscribe [::subs/effective-filter :area])]
    (when-not (= area :all)
      [bubble :area area (case area
                           :machtfit (t {:de "machtfit-Angebote"
                                         :en "machtfit offers"})
                           :company (t @(rf/subscribe [:get [:ui :company-area-name]])))
       #(rf/dispatch [::events/remove-filter :area])])))

(defn- product-type-bubble []
  (when-let [product-type @(rf/subscribe [::subs/effective-filter :product-types])]
    [bubble :product-types product-type (case product-type
                                          #{"product"} (t {:de "Nur Produkte"
                                                           :en "Only products"})
                                          #{"with-location"} (t {:de "Nur Angebote vor Ort"
                                                                 :en "Only on-site offers"})
                                          #{"online"} (t {:de "Nur Online Angebote"
                                                          :en "Only online offers"}))
     #(rf/dispatch [::toggle-product-types (first product-type)])]))

(defn- distance-bubble []
  (when-let [distance @(rf/subscribe [::subs/effective-filter :distance])]
    (when (not= distance 70)
      [bubble :distance distance (str distance " km") #(rf/dispatch [::events/filter-distance-changed 70])])))

(defn- price-min-bubble []
  (when-let [price-min @(rf/subscribe [::subs/effective-filter :price-min])]
    [bubble :price-min price-min (str (t {:de "mehr als "
                                          :en "more than "})
                                      price-min
                                      " €")
     #(rf/dispatch [::events/filter-price-range-changed {}])]))

(defn- price-max-bubble []
  (when-let [price-max @(rf/subscribe [::subs/effective-filter :price-max])]
    [bubble :price-min price-max (str (t {:de "bis "
                                          :en "up to "})
                                      price-max
                                      " €")
     #(rf/dispatch [::events/filter-price-range-changed {}])]))

(defn- favorites-bubble []
  (when @(rf/subscribe [::subs/effective-filter :favorites])
    [bubble :favorites true (t {:de "Favoriten"
                                :en "Favorites"})
     #(rf/dispatch [::events/remove-filter :favorites])]))

(defn- keyword-bubble []
  (when-let [search @(rf/subscribe [::subs/effective-filter :keyword])]
    (when-not (str/blank? search)
      [bubble :keyword search search #(rf/dispatch [::events/keyword-filter-changed ""])])))

(defn filter-bubbles []
  (let [balance-phrasing (if (= @(rf/subscribe [:get [:user :company-phrasing]]) "employer")
                           {:de "mit Arbeitgeberzuschuss"
                            :en "with employer's allowance"}
                           {:de "mit Guthabenzuschuss"
                            :en "covered by my balance"})
        show-marketplace? @(rf/subscribe [::subs/show-marketplace?])
        has-company-area? @(rf/subscribe [:get [:user :has-company-area?]])]
    [:div.filter-bubbles-container
     [:div.filter-bubbles
      (when (and show-marketplace? has-company-area?)
        [area-bubble])
      [product-type-bubble]
      [category-bubbles]
      [distance-bubble]
      [price-min-bubble]
      [price-max-bubble]
      [favorites-bubble]
      [multi-bubble :days {:week {:de "Werktags"
                                  :en "Weekdays"}
                           :weekend {:de "Wochenende"
                                     :en "Weekend"}}]
      [multi-bubble :weekdays (into {} (vec (for [weekday-int (keys weekdays)]
                                              {weekday-int (-> (weekday weekday-int)
                                                               :short)})))]
      [single-bubble--time
       :starts-after
       (t {:de "ab"
           :en "after"})
       (:starts-after filter/default-filter)]
      [single-bubble--time
       :starts-before
       (t {:de "bis"
           :en "before"})
       (:starts-before filter/default-filter)]
      [multi-bubble :times {:morning {:de "Vormittags (bis 12:00)"
                                      :en "Morning (until noon)"}
                            :afternoon {:de "Nachmittags (12:00 - 17:00)"
                                        :en "Afternoon (noon - 5 pm)"}
                            :evening {:de "Abends (ab 17:00)"
                                      :en "Evening (from 5 pm)"}}]
      [multi-bubble :labels]
      [keyword-bubble]
      [single-bubble :free-of-charge? {:de "Nur kostenlose Angebote"
                                       :en "Only free offers"}]
      [single-bubble :estg3no34-subsidizable? {:de "Präventionsangebote"
                                               :en "Prevention offer"}]
      [single-bubble :streams {:de "Streaming-Angebot"
                               :en "Streaming offer"}]
      [single-bubble :apps {:de "Apps"
                            :en "Apps"}]
      [single-bubble :subsidy balance-phrasing]
      [single-bubble :mf-offer {:de "machtfit-Preisvorteil"
                                :en "machtfit price advantage"}]
      [single-bubble :promotions {:de "Nur Aktionsangebote"
                                  :en "Only promotional offers"}]
      [multi-bubble :age {:under-17 {:de "bis 17 Jahre"
                                     :en "up to 17 years"}
                          :over-50 {:de "ab 50 Jahre"
                                    :en "from 50 years"}}]
      [multi-bubble :gender {:men {:de "Nur für Männer"
                                   :en "For men only"}
                             :women {:de "Nur für Frauen"
                                     :en "For women only"}}]

      [:div.reset
       [:button.filter-reset
        (merge {:style {:margin "0 0 0 32px"
                        :font-size "16px"
                        :color "#000"
                        :width "auto"}}
               (update
                (routes/attrs-link-to-page
                 {:id :catalog})
                :on-click
                (fn [on-click]
                  #(do
                     (rf/dispatch-sync [::events/set-filter-action {:type "delete_all"
                                                                    :details {:is_reset true}}])
                     (on-click)))))
        [:span (t {:de "Alle löschen"
                   :en "Delete all"})]]]]]))

(rf/reg-event-fx ::fetch-choices
  (fn [{:keys [db]} [_ search-string on-success]]
    {:post (api/bare-request db {:data `[(:geocode-suggestions {:searchstring ~search-string} :geocode-suggestions)]
                                 :on-success on-success})}))

(rf/reg-event-fx ::choose-suggestion
  (fn [{:keys [db]} [_ {:keys [id] :as _suggestion}]]
    {:db (assoc-in db [:ui :products-location-lookup-running?] true)
     :post (api/bare-request db {:data `[(:geocode-lookup-by-id {:here_id ~id} :geocode-lookup-by-id)]
                                 :on-success #(rf/dispatch [::set-products-location (get-in % [:result :geocode-lookup-by-id :result])])})}))

(rf/reg-event-fx ::set-products-location
  (fn [{:keys [db]} [_ location]]
    (location/set-products-location-fx db location)))

(defn highlight-suggestion [{:keys [formatted-address highlights]}]
  (let [last-index (count formatted-address)
        highlight-borders (concat [0]
                                  (mapcat (juxt :start :end) highlights)
                                  [last-index])
        sections (partition 2 1 highlight-borders)]
    (keep-indexed
     (fn [i [start end]]
       (when-not (= start end)
         (if (odd? i) [:strong {:key i} (subs formatted-address start end)]
             (subs formatted-address start end))))
     sections)))

(defn location [products-location _]
  (let [location-input (r/atom (:formatted-address products-location))
        suggestions (r/atom nil)
        fetch-suggestions (debounce (fn [entered-string]
                                      (rf/dispatch [::fetch-choices entered-string
                                                    (fn [response]
                                                      (let [matches (get-in response [:result :geocode-suggestions :result])]
                                                        (reset! suggestions matches)))]))
                                    250)]
    (r/create-class
     {:component-did-update
      (fn [this old-argv]
        (let [[_ new-products-location is-open?] (r/argv this)
              [_ old-products-location was-open?] old-argv]
          (when-not (= old-products-location new-products-location)
            (reset! location-input (:formatted-address new-products-location)))
          (when-not (= is-open? was-open?)
            (reset! location-input (:formatted-address new-products-location)))))
      :reagent-render
      (fn [products-location open?]
        (let [home-location @(rf/subscribe [:get [:user :home-location]])
              work-location @(rf/subscribe [:get [:user :place-of-business-location]])

              current-distance @(rf/subscribe [::subs/effective-filter :distance])
              distance-customized? (and (some? current-distance) (not= current-distance 70))
              resolved-geo-is-home? (locations-equal? home-location products-location)
              number-of-active-filters (count (filter identity [distance-customized? (and (not resolved-geo-is-home?)
                                                                                          home-location)]))
              active? (> number-of-active-filters 0)]
          [:div.filter-dropdown
           {:class (when open?
                     "open")
            :style {:border "none"}}
           [:button
            {:data-ph-capture-attribute-ui_id (tracking/compose-ui-id "filterbar" "location")
             :class (cond-> ["filter-dropdown__button"]
                      open? (conj "open")
                      active? (conj "active"))
             :on-click (fn [e]
                         (.stopPropagation e)
                         (rf/dispatch [::events/toggle-filter-dropdown :location]))}

            (str (t {:de "Ort"
                     :en "Location"})
                 (when active?
                   (str " (" number-of-active-filters ")")))]
           [:div.filter-dropdown__content {:class (when open?
                                                    "open location")
                                           :on-click (fn [e] (.stopPropagation e))}
            [:header
             [:h5 (t {:de "Ort"
                      :en "Location"})]
             [:button {:on-click #(do (rf/dispatch-sync [::events/set-filter-action {:type "filter_bar"
                                                                                     :details {:section "location"
                                                                                               :filter_key :distance
                                                                                               :is_reset true}}])
                                      (rf/dispatch [::events/filter-distance-changed 70])
                                      (rf/dispatch [::set-products-location (or home-location work-location)]))
                       :disabled (not active?)
                       :data-ph-capture-attribute-ui_id (tracking/compose-ui-id "filterbar" "location" "reset")}
              (t {:de "zurücksetzen"
                  :en "reset"})]]
            [:h6 (t {:de "Adresse"
                     :en "Address"})]
            [:div.input-wrapper
             {:data-ph-capture-attribute-ui_id (tracking/compose-ui-id "filterbar" "location" "search")
              :class (when @(rf/subscribe [:get [:ui :products-location-lookup-running?]]) "running")}
             [:input.location-search
              {:id "location-search-input"
               :class (when (seq @suggestions) "active")
               :disabled @(rf/subscribe [:get [:ui :products-location-lookup-running?]])
               :defaultValue (:formatted-address products-location)
               :value (if @(rf/subscribe [:get [:ui :products-location-lookup-running?]])
                        ""
                        @location-input)
               :on-change #(let [newval (-> % .-target .-value)]
                             (reset! location-input newval)
                             (if (> (count newval) 2)
                               (fetch-suggestions newval)
                               (reset! suggestions nil)))
               :on-key-up #(when (and (= (.-key %) "Enter") (seq @suggestions))
                             (rf/dispatch-sync [::choose-suggestion (first @suggestions)])
                             (reset! suggestions nil)
                             (-> % .-target .blur))
               :type "text"
               :placeholder (if @(rf/subscribe [:get [:ui :products-location-lookup-running?]])
                              (t {:de "Adresse wird gesetzt…"
                                  :en "Location is being set…"})
                              (t {:de "Straße, Ort, Postleitzahl"
                                  :en "Street, town, zipcode"}))}]
             (when (seq @location-input)
               [:div.search-reset
                {:style {:top 0}
                 :on-click #(do (reset! location-input "")
                                (.focus (.getElementById js/document "location-search-input")))}])
             (when (= [] @suggestions) [:div.message (t {:de "Keine passenden Adressen gefunden"
                                                         :en "No matching addresses"})])
             (when (seq @suggestions)
               [:div.autocomplete
                [:ul
                 (for [suggestion @suggestions]
                   ^{:key suggestion} [:li
                                       [:button
                                        {:on-click #(do
                                                      (reset! suggestions nil)
                                                      (rf/dispatch-sync [::choose-suggestion suggestion]))}
                                        (highlight-suggestion suggestion)]])]])]
            [:div.filter-row
             [:button
              {:class (when (locations-equal? work-location products-location)
                        "active")
               :data-ph-capture-attribute-ui_id (tracking/compose-ui-id "filterbar" "location" "work-button")
               :on-click #(rf/dispatch [::set-products-location work-location])}
              (t {:de "Arbeit"
                  :en "Work"})]
             [:button
              {:class (when resolved-geo-is-home?
                        "active")
               :data-ph-capture-attribute-ui_id (tracking/compose-ui-id "filterbar" "location" "home-button")
               :on-click #(rf/dispatch [::set-products-location home-location])
               :disabled (not home-location)}
              (t {:de "Zu Hause"
                  :en "Home"})]]
            (when-not home-location
              [:a {:href "/account/profile/"}
               (t {:de "Gib jetzt deine Wohnadresse an."
                   :en "Enter your home address now."})])

            [:h6 (t {:de "Entfernung"
                     :en "Distance"})]
            [:div.filter-row
             (doall
              (for [distance [1 5 10 30 70]]
                ^{:key (str "distance-" distance)}
                [:button
                 {:class (when (= distance current-distance)
                           "active")
                  :data-ph-capture-attribute-ui_id (tracking/compose-ui-id "filterbar" "location" "distance" (str distance))
                  :on-click #(rf/dispatch [::events/filter-distance-changed distance {:type "filter_bar"
                                                                                      :details {:section "location"
                                                                                                :subsection "distance"}}])}
                 (str distance " km")]))]

            [:button.primary
             {:data-ph-capture-attribute-ui_id (tracking/compose-ui-id "filterbar" "location" "apply")
              :on-click (fn [_event]
                          (rf/dispatch [::events/toggle-filter-dropdown :location])
                          (js/window.scrollTo 0 0))} (t {:de "Anwenden"
                                                         :en "Apply"})]]]))})))
(rf/reg-event-fx ::reset-categories-filter
  (fn [{:keys [db]} [_event categories-to-remove]]
    (events/update-filter-into-unspecific-preset
     db
     #(update % :categories
              (fn [categories]
                (set/difference categories categories-to-remove))))))

(rf/reg-sub ::category-filter-set?
  :<- [::subs/effective-filter :categories]
  (fn [category-filter [_ pertinent-categories]]
    (seq (set/intersection category-filter pertinent-categories))))

(defn- category-dropdown [_props]
  (let [search-input (r/atom "")]
    (fn [{:keys [placeholder dropdown-key categories active-categories-count checked-items title]}]
      (let [open? (= @(rf/subscribe [:get [:ui :visible-dropdown]]) dropdown-key)
            active? (pos? active-categories-count)
            dropdown-input-id (str (name dropdown-key) "-category-search-input")
            grouped-items (->> categories
                               (map (fn [de]
                                      {:de de
                                       :en (get statics/category-translations de)}))
                               (filter #(gstring/caseInsensitiveContains (or (t %) "") @search-input))
                               (group-by #(first (t %)))
                               sort)]
        [:div.filter-dropdown
         {:class (when open?
                   "open")
          :style {:border "none"}}
         [:button {:class (cond-> ["filter-dropdown__button"]
                            open? (conj "open")
                            active? (conj "active"))
                   :data-ph-capture-attribute-ui_id (tracking/compose-ui-id "filterbar" dropdown-key)
                   :on-click #(do
                                (machtfit/stop-event %)
                                (rf/dispatch [::events/toggle-filter-dropdown dropdown-key]))}
          (t title)
          (when active?
            (str " (" active-categories-count ")"))]

         [:section.filter-dropdown__content.letter-container
          {:class (when open?
                    "open")}
          [:header
           [:h5 (t title)]
           [:button {:on-click #(do
                                  (rf/dispatch-sync [::events/set-filter-action {:type "filter_bar"
                                                                                 :details {:section dropdown-key
                                                                                           :filter_key :categories
                                                                                           :is_reset true}}])
                                  (rf/dispatch [::reset-categories-filter categories]))
                     :disabled (not @(rf/subscribe [::category-filter-set? categories]))
                     :data-ph-capture-attribute-ui_id (tracking/compose-ui-id "filterbar" dropdown-key "reset")}
            (t {:de "zurücksetzen"
                :en "reset"})]]
          [:div.input-wrapper
           {:data-ph-capture-attribute-ui_id (tracking/compose-ui-id "filterbar" dropdown-key "search")}
           [:input.categories-search
            {:id dropdown-input-id
             :type "text"
             :value @search-input
             :on-change #(reset! search-input (-> % .-target .-value))
             :placeholder (t placeholder)}]
           (when (seq @search-input)
             [:div.search-reset {:on-click #(do (reset! search-input "")
                                                (.focus (.getElementById js/document dropdown-input-id)))}])]
          [:section.scroll
           (doall
            (for [[letter categories] grouped-items]
              ^{:key letter}
              [:<>
               [:h6 letter]
               [:div.filter-row
                (doall
                 (for [category categories
                       :let [name (:de category)
                             checked? (boolean (checked-items name))
                             label (t category)
                             event #(rf/dispatch [::events/checkbox-clicked (:de category) :categories (not checked?) dropdown-key])]]
                   ^{:key category}
                   [:label {:data-ph-capture-attribute-ui_id (tracking/compose-ui-id "filterbar" dropdown-key "checkbox" name)
                            :class (when checked?
                                     "active")}
                    label
                    [:input {:type "checkbox"
                             :name name
                             :class "checkbox"
                             :checked checked?
                             :on-change event}]]))]]))]
          [:button.primary
           {:data-ph-capture-attribute-ui_id (tracking/compose-ui-id "filterbar" dropdown-key "apply")
            :on-click (fn []
                        (rf/dispatch [::events/toggle-filter-dropdown dropdown-key])
                        (js/window.scrollTo 0 0))} (t {:de "Anwenden"
                                                       :en "Apply"})]]]))))

(rf/reg-sub ::activity-categories
  :<- [::subs/non-empty-categories-in-current-area]
  (fn [non-empty-categories-in-current-area _]
    (set/difference non-empty-categories-in-current-area statics/product-categories)))

(rf/reg-sub ::active-activity-categories-count
  :<- [::subs/effective-filter :categories]
  :<- [::activity-categories]
  (fn [[categories activity-categories] _]
    (count (set/intersection
            categories
            (set activity-categories)))))

(defn activities []
  [category-dropdown {:dropdown-key :activities
                      :categories @(rf/subscribe [::activity-categories])
                      :active-categories-count @(rf/subscribe [::active-activity-categories-count])
                      :checked-items @(rf/subscribe [::subs/effective-filter :categories])
                      :title {:de "Aktivitäten"
                              :en "Activities"}
                      :placeholder {:de "Aktivitäten durchsuchen"
                                    :en "Search activities"}}])

(rf/reg-sub ::product-categories
  :<- [::subs/non-empty-categories-in-current-area]
  (fn [non-empty-categories-in-current-area _]
    (set/intersection non-empty-categories-in-current-area statics/product-categories)))

(rf/reg-sub ::active-product-categories-count
  :<- [::subs/effective-filter :categories]
  :<- [::product-categories]
  (fn [[categories product-categories] _]
    (count (set/intersection
            categories
            (set product-categories)))))

(defn products []
  (let [categories @(rf/subscribe [::product-categories])]
    (when (seq categories)
      [category-dropdown {:dropdown-key :products
                          :categories categories
                          :active-categories-count @(rf/subscribe [::active-product-categories-count])
                          :checked-items @(rf/subscribe [::subs/effective-filter :categories])
                          :title {:de "Produkte"
                                  :en "Products"}
                          :placeholder {:de "Produkte durchsuchen"
                                        :en "Search products"}}])))

(rf/reg-event-fx ::reset-weekday-and-time-filter
  (fn [{:keys [db]} [_event]]
    (events/act-on-filter db #(dissoc % :weekdays :starts-after :starts-before) {:type "filter_bar"
                                                                                 :details {:section "time"
                                                                                           :is_reset true}})))

(rf/reg-sub ::weekday-or-time-filter-set?
  :<- [:get [:filter]]
  (fn [effective-filter _]
    (or (seq (:weekdays effective-filter))
        (:starts-after effective-filter)
        (:starts-before effective-filter))))

(defn weekday-button [weekday-int filter-weekdays]
  [:button {:class (when (utils/in? filter-weekdays weekday-int)
                     "active")
            :data-ph-capture-attribute-ui_id (tracking/compose-ui-id "filterbar" "weekday" (str weekday-int))
            :on-click #(rf/dispatch [::events/checkbox-clicked weekday-int
                                     :weekdays
                                     (not (utils/in? filter-weekdays weekday-int))
                                     "time"])}
   (-> (weekday weekday-int)
       :short
       t)])

(rf/reg-event-fx ::set-starts-after
  (fn [{:keys [db]} [_ starts-after]]
    (events/act-on-filter db
                          #(assoc % :starts-after starts-after)
                          {:type "filter_bar"
                           :details {:filter_key "starts-after"
                                     :value starts-after
                                     :was-added (not= starts-after "00:00")}})))

(rf/reg-event-fx ::set-starts-before
  (fn [{:keys [db]} [_ starts-before]]
    (events/act-on-filter db
                          #(assoc % :starts-before starts-before)
                          {:type "filter_bar"
                           :details {:filter_key "starts-before"
                                     :value starts-before
                                     :was-added (not= starts-before "23:59")}})))

(defn- active-time-filter-count [filter-weekdays filter-starts-after filter-starts-before]
  (let [filter-weekdays-set? (seq filter-weekdays)
        filter-starts-after-set? (and filter-starts-after
                                      (not= filter-starts-after (:starts-after filter/default-filter)))
        filter-starts-before-set? (and filter-starts-before
                                       (not= filter-starts-before (:starts-before filter/default-filter)))]
    (when filter-weekdays-set?
      (cond-> (count filter-weekdays)

        filter-starts-after-set? (+ 1)

        filter-starts-before-set? (+ 1)))))

(defn time-and-day []
  (let [filter-weekdays @(rf/subscribe [::subs/effective-filter :weekdays])
        filter-starts-after @(rf/subscribe [::subs/effective-filter :starts-after])
        filter-starts-before @(rf/subscribe [::subs/effective-filter :starts-before])
        open? (= @(rf/subscribe [:get [:ui :visible-dropdown]]) :time-and-day)
        active-filter-count (active-time-filter-count filter-weekdays filter-starts-after filter-starts-before)
        active? (pos? active-filter-count)]
    [:div.filter-dropdown
     {:class (when open?
               "open")
      :style {:border "none"}}
     [:button {:class (cond-> ["filter-dropdown__button"]
                        open? (conj "open")
                        active? (conj "active"))
               :data-ph-capture-attribute-ui_id (tracking/compose-ui-id "filterbar" "time-and-day")
               :on-click #(do
                            (machtfit/stop-event %)
                            (rf/dispatch [::events/toggle-filter-dropdown :time-and-day]))}

      (str (t {:de "Zeit"
               :en "Time"})
           (when active?
             (str " (" active-filter-count ")")))]

     [:section.filter-dropdown__content
      {:class (when open?
                "open")}
      [:header
       [:h5 (t {:de "Zeit"
                :en "Time"})]
       [:button {:on-click #(rf/dispatch [::reset-weekday-and-time-filter])
                 :disabled (not @(rf/subscribe [::weekday-or-time-filter-set?]))
                 :data-ph-capture-attribute-ui_id (tracking/compose-ui-id "filterbar" "time" "reset")}
        (t {:de "zurücksetzen"
            :en "reset"})]]
      [:h6 (t {:de "Wochentag"
               :en "Weekday"})]
      [:div.filter-row
       {:style {:flex-direction "row"}}
       (for [day (keys weekdays)]
         ^{:key day}
         [weekday-button day filter-weekdays])]
      [:h6 (t {:de "Zeit"
               :en "Time"})]
      (if (empty? filter-weekdays)
        [:span (t {:de "Wähle deine bevorzugten Wochentage aus und lege dann deinen Wunschzeitraum fest."
                   :en "Select your preferred days of the week and then set your desired time period."})]
        [:div.filter-time
         [:input {:type "time"
                  :value @(rf/subscribe [::subs/effective-filter :starts-after])
                  :name :starts-after
                  :on-change #(rf/dispatch [::set-starts-after (-> % .-target .-value)])}]
         [:span (t {:de "bis"
                    :en "till"})]
         [:input {:type "time"
                  :value @(rf/subscribe [::subs/effective-filter :starts-before])
                  :name :starts-before
                  :on-change #(rf/dispatch [::set-starts-before (-> % .-target .-value)])}]])
      [:button.primary
       {:data-ph-capture-attribute-ui_id (tracking/compose-ui-id "filterbar" "time-and-day" "apply")
        :on-click (fn []
                    (rf/dispatch [::events/toggle-filter-dropdown :time-and-day])
                    (js/window.scrollTo 0 0))} (t {:de "Anwenden"
                                                   :en "Apply"})]]]))

(rf/reg-event-fx ::toggle-product-types
  (fn [{:keys [db]} [_event product-type]]
    (events/update-filter db #(if (= (:product-types %) #{product-type})
                                (dissoc % :product-types)
                                (assoc % :product-types #{product-type})))))

(rf/reg-event-fx ::reset-product-types-filter
  (fn [{:keys [db]} [_event]]
    (events/update-filter-into-unspecific-preset
     db #(dissoc % :product-types))))

(defn product-types []
  (let [open? (= @(rf/subscribe [:get [:ui :visible-dropdown]]) :product-types)
        machtfit-area-only-online? (not @(rf/subscribe [:get [:user :machtfit-area-only-online?]]))
        active-product-type @(rf/subscribe [::subs/effective-filter :product-types])
        active-filter-count (count active-product-type)
        active? (pos? active-filter-count)]
    [:div.filter-dropdown
     {:class (when open?
               "open")
      :style {:border "none"}}
     [:button {:class (cond-> ["filter-dropdown__button"]
                        open? (conj "open")
                        active? (conj "active"))
               :data-ph-capture-attribute-ui_id (tracking/compose-ui-id "filterbar" "type")
               :on-click #(do
                            (machtfit/stop-event %)
                            (rf/dispatch [::events/toggle-filter-dropdown :product-types]))}

      (str (t {:de "Typ"
               :en "Type"})
           (when active?
             (str " (" active-filter-count ")")))]

     [:section.filter-dropdown__content
      {:class (when open?
                "open")}
      [:header
       [:h5 (t {:de "Typ"
                :en "Type"})]
       [:button {:on-click #(do
                              (rf/dispatch-sync [::events/set-filter-action {:type "filter_bar"
                                                                             :details {:section "type"
                                                                                       :filter_key :product-types
                                                                                       :is_reset true}}])
                              (rf/dispatch [::reset-product-types-filter]))
                 :disabled (not active-product-type)
                 :data-ph-capture-attribute-ui_id (tracking/compose-ui-id "filterbar" "type" "reset")}
        (t {:de "zurücksetzen"
            :en "reset"})]]

      [:div.filter-row
       {:data-ph-capture-attribute-ui_id (tracking/compose-ui-id "filterbar" "type" "online")}
       [:label {:class (when (= #{"online"} active-product-type)
                         "active")}
        (t {:de "Nur Online Angebote"
            :en "Only online offers"})
        [:input {:type "checkbox"
                 :class "switch"
                 :name :online
                 :checked (= #{"online"} active-product-type)
                 :on-change #(do
                               (rf/dispatch-sync [::events/set-filter-action {:type "filter_bar"
                                                                              :details {:section "type"
                                                                                        :filter_key :product-types
                                                                                        :value "online"
                                                                                        :was_added (not (contains? active-product-type "online"))}}])
                               (rf/dispatch [::toggle-product-types "online"]))}]]]

      (when machtfit-area-only-online?
        [:div.filter-row
         {:data-ph-capture-attribute-ui_id (tracking/compose-ui-id "filterbar" "type" "with-location")}
         [:label {:class (when (= #{"with-location"} active-product-type)
                           "active")}
          (t {:de "Nur Angebote vor Ort"
              :en "Only on-site offers"})
          [:input {:type "checkbox"
                   :class "switch"
                   :name :with-location
                   :checked (= #{"with-location"} active-product-type)
                   :on-change #(do
                                 (rf/dispatch-sync [::events/set-filter-action {:type "filter_bar"
                                                                                :details {:section "type"
                                                                                          :filter_key :product-types
                                                                                          :value "with-location"
                                                                                          :was_added (not (contains? active-product-type "with-location"))}}])
                                 (rf/dispatch [::toggle-product-types "with-location"]))}]]])
      (when (seq @(rf/subscribe [::product-categories]))
        [:div.filter-row
         {:data-ph-capture-attribute-ui_id (tracking/compose-ui-id "filterbar" "type" "products")}
         [:label {:class (when (= #{"product"} active-product-type)
                           "active")}
          (t {:de "Nur Produkte"
              :en "Only products"})
          [:input {:type "checkbox"
                   :class "switch"
                   :name :products
                   :checked (= #{"product"} active-product-type)
                   :on-change #(do
                                 (rf/dispatch-sync [::events/set-filter-action {:type "filter_bar"
                                                                                :details {:section "type"
                                                                                          :filter_key :product-types
                                                                                          :value "product"
                                                                                          :was_added (not (contains? active-product-type "product"))}}])
                                 (rf/dispatch [::toggle-product-types "product"]))}]]])

      [:button.primary
       {:data-ph-capture-attribute-ui_id (tracking/compose-ui-id "filterbar" "type" "apply")
        :on-click (fn []
                    (rf/dispatch [::events/toggle-filter-dropdown :product-types])
                    (js/window.scrollTo 0 0))} (t {:de "Anwenden"
                                                   :en "Apply"})]]]))

(rf/reg-event-fx ::toggle-area
  (fn [{:keys [db]} [_event area]]
    (events/act-on-filter db #(assoc % :area area) {:type "filter_bar"
                                                    :details {:section "area"
                                                              :filter_key "area"
                                                              :value area}})))

(defn get-area-name [area]
  (case area
    :machtfit (t {:de "machtfit-Angebote"
                  :en "machtfit offers"})
    :company (t @(rf/subscribe [:get [:ui :company-area-name]]))
    :all (t {:de "Alle Angebote"
             :en "All offers"})))

(defn areas []
  (let [open? (= @(rf/subscribe [:get [:ui :visible-dropdown]]) :area)
        active-area @(rf/subscribe [::subs/effective-filter :area])
        active? (and (not (nil? active-area)) (not= active-area (:area filter/default-filter)))]
    [:div.filter-dropdown
     {:class (when open?
               "open")
      :style {:border "none"}}
     [:button {:class (cond-> ["filter-dropdown__button"]
                        open? (conj "open")
                        active? (conj "active"))
               :data-ph-capture-attribute-ui_id (tracking/compose-ui-id "filterbar" "area")
               :on-click #(do
                            (machtfit/stop-event %)
                            (rf/dispatch [::events/toggle-filter-dropdown :area]))}

      (if active?
        (get-area-name active-area)
        (get-area-name :all))]

     [:section.filter-dropdown__content
      {:class (when open?
                "open")}
      [:header
       [:h5 (t {:de "Angebote"
                :en "Offers"})]
       [:button {:on-click #(do
                              (rf/dispatch-sync [::events/set-filter-action {:type "filter_bar"
                                                                             :details {:section "area"
                                                                                       :filter-key "area"
                                                                                       :was_added false
                                                                                       :is_reset true}}])
                              (rf/dispatch [::events/remove-filter :area]))
                 :disabled (not active-area)
                 :data-ph-capture-attribute-ui_id (tracking/compose-ui-id "filterbar" "area" "reset")}
        (t {:de "zurücksetzen"
            :en "reset"})]]

      [:div.filter-row
       {:data-ph-capture-attribute-ui_id (tracking/compose-ui-id "filterbar" "area" "all")}
       [:label {:class (when (= :all active-area)
                         "active")}
        (get-area-name :all)
        [:input {:type "checkbox"
                 :class "switch"
                 :name :all
                 :checked (= :all active-area)
                 :on-change #(rf/dispatch [::toggle-area :all])}]]]

      [:div.filter-row
       {:data-ph-capture-attribute-ui_id (tracking/compose-ui-id "filterbar" "area" "machtfit")}
       [:label {:class (when (= :machtfit active-area)
                         "active")}
        (get-area-name :machtfit)
        [:input {:type "checkbox"
                 :class "switch"
                 :name :machtfit
                 :checked (= :machtfit active-area)
                 :on-change #(rf/dispatch [::toggle-area :machtfit])}]]]
      [:div.filter-row
       {:data-ph-capture-attribute-ui_id (tracking/compose-ui-id "filterbar" "area" "company")}
       [:label {:class (when (= :company active-area)
                         "active")}
        (get-area-name :company)
        [:input {:type "checkbox"
                 :class "switch"
                 :name :company
                 :checked (= :company active-area)
                 :on-change #(rf/dispatch [::toggle-area :company])}]]]

      [:button.primary
       {:data-ph-capture-attribute-ui_id (tracking/compose-ui-id "filterbar" "area" "apply")
        :on-click (fn []
                    (rf/dispatch [::events/toggle-filter-dropdown :area])
                    (js/window.scrollTo 0 0))} (t {:de "Anwenden"
                                                   :en "Apply"})]]]))

(rf/reg-event-fx ::toggle-price-filter
  (fn [{:keys [db]} [_event active-key]]
    (events/act-on-filter
     db
     #(-> %
          (merge {:free-of-charge? false
                  :subsidy false
                  :mf-offer false
                  :estg3no34-subsidizable? false})
          (assoc active-key (not (get % active-key))))
     {:type "filter_bar"
      :details {:section "price"
                :filter_key active-key
                :was_added (not (get-in db [:filter active-key]))}})))

(rf/reg-event-fx ::toggle-promotions-filter
  (fn [{:keys [db]} [_event]]
    (events/act-on-filter
     db
     #(update % :promotions not)
     {:type "filter_bar"
      :details {:section "price"
                :filter_key :promotions
                :was_added (not (get-in db [:filter :promotions]))}})))

(rf/reg-event-fx ::reset-price-filter
  (fn [{:keys [db]} [_event]]
    (events/update-filter-into-unspecific-preset
     db
     #(dissoc % :free-of-charge? :subsidy :mf-offer :promotions :price-min :price-max :estg3no34-subsidizable?))))

(rf/reg-sub ::price-filter-set?
  :<- [::subs/effective-filter]
  (fn [effective-filter _]
    (some identity
          (vals (select-keys effective-filter
                             [:free-of-charge?
                              :subsidy
                              :mf-offer
                              :promotions
                              :price-min
                              :price-max
                              :estg3no34-subsidizable?])))))

(defn price []
  (let [open? (= @(rf/subscribe [:get [:ui :visible-dropdown]]) :price)
        subsidy-filter? @(rf/subscribe [::subs/effective-filter :subsidy])
        mf-offer? @(rf/subscribe [::subs/effective-filter :mf-offer])
        estg3no34-subsidizable-filter? @(rf/subscribe [::subs/effective-filter :estg3no34-subsidizable?])
        free-of-charge? @(rf/subscribe [::subs/effective-filter :free-of-charge?])
        promotions? @(rf/subscribe [::subs/effective-filter :promotions])
        promotions-filter-enabled? @(rf/subscribe
                                     [::feature/feature-flag-enabled?
                                      :dev_3558_has_promotions_filter_enabled])
        current-price-min @(rf/subscribe [::subs/effective-filter :price-min])
        current-price-max @(rf/subscribe [::subs/effective-filter :price-max])
        active-filter-count (count
                             (filter identity
                                     [mf-offer?
                                      subsidy-filter?
                                      free-of-charge?
                                      promotions?
                                      estg3no34-subsidizable-filter?
                                      (or current-price-min
                                          current-price-max)]))
        active? (pos? active-filter-count)
        balance-phrasing (if (= @(rf/subscribe [:get [:user :company-phrasing]]) "employer")
                           {:de "Nur mit Arbeitgeberzuschuss"
                            :en "Only with employer's allowance"}
                           {:de "Nur mit Guthabenzuschuss"
                            :en "Only covered by balance"})]
    [:div.filter-dropdown
     {:class (when open?
               "open")
      :style {:border "none"}}
     [:button {:class (cond-> ["filter-dropdown__button"]
                        open? (conj "open")
                        active? (conj "active"))
               :data-ph-capture-attribute-ui_id (tracking/compose-ui-id "filterbar" "price")
               :on-click #(do
                            (machtfit/stop-event %)
                            (rf/dispatch [::events/toggle-filter-dropdown :price]))}

      (str (t {:de "Preis"
               :en "Price"})
           (when active?
             (str " (" active-filter-count ")")))]

     [:section.filter-dropdown__content
      {:class (when open?
                "open")}
      [:header
       [:h5 (t {:de "Preis"
                :en "Price"})]
       [:button {:on-click #(do
                              (rf/dispatch-sync [::events/set-filter-action {:type "filter_bar"
                                                                             :details {:section "price"
                                                                                       :is_reset true}}])
                              (rf/dispatch [::reset-price-filter]))
                 :disabled (not @(rf/subscribe [::price-filter-set?]))
                 :data-ph-capture-attribute-ui_id (tracking/compose-ui-id "filterbar" "price" "reset")}
        (t {:de "zurücksetzen"
            :en "reset"})]]
      [:h6 (t {:de "Preis"
               :en "Price"})]
      [:div.filter-row
       {:style {:flex-direction "row"}}
       [:button
        {:class (when (= current-price-max 50)
                  "active")
         :data-ph-capture-attribute-ui_id (tracking/compose-ui-id "filterbar" "price" "<=50")
         :on-click #(do
                      (rf/dispatch-sync [::events/set-filter-action {:type "filter_bar"
                                                                     :section "price"
                                                                     :price-range {:max 50}}])
                      (rf/dispatch [::events/filter-price-range-changed (if (= current-price-max 50)
                                                                          {}
                                                                          {:max 50})]))}
        (t {:de "bis 50 €"
            :en "up to 50 €"})]
       [:button
        {:class (when (= current-price-max 100)
                  "active")
         :data-ph-capture-attribute-ui_id (tracking/compose-ui-id "filterbar" "price" "<=100")
         :on-click #(do
                      (rf/dispatch-sync [::events/set-filter-action {:type "filter_bar"
                                                                     :section "price"
                                                                     :price-range {:max 100}}])
                      (rf/dispatch [::events/filter-price-range-changed (if (= current-price-max 100)
                                                                          {}
                                                                          {:max 100})]))}
        (t {:de "bis 100 €"
            :en "up to 100 €"})]
       [:button
        {:class (when (= current-price-min 100)
                  "active")
         :data-ph-capture-attribute-ui_id (tracking/compose-ui-id "filterbar" "price" ">100")
         :on-click #(do
                      (rf/dispatch-sync [::events/set-filter-action {:type "filter_bar"
                                                                     :section "price"
                                                                     :price-range {:min 100}}])
                      (rf/dispatch [::events/filter-price-range-changed (if (= current-price-min 100)
                                                                          {}
                                                                          {:min 100})]))}
        (t {:de "mehr als 100 €"
            :en "more than 100 €"})]]

      [:h6 (t {:de "Preisvorteil"
               :en "Price advantage"})]

      (when promotions-filter-enabled?
        [:div.filter-row
         {:data-ph-capture-attribute-ui_id (tracking/compose-ui-id "filterbar" "price" "promotions")}
         [:label {:class (when promotions?
                           "active")}
          (t {:de "Nur Aktionsangebote"
              :en "Only promotional offers"})
          [:input {:type "checkbox"
                   :class "switch"
                   :name :promotions
                   :checked (boolean promotions?)
                   :on-change #(rf/dispatch [::toggle-promotions-filter])}]]])

      [:div.filter-row
       {:data-ph-capture-attribute-ui_id (tracking/compose-ui-id "filterbar" "price" "subsidized")}
       [:label {:class (when subsidy-filter?
                         "active")}
        (t balance-phrasing)
        [:input {:type "checkbox"
                 :class "switch"
                 :name :subsidy
                 :checked (boolean subsidy-filter?)
                 :on-change #(rf/dispatch [::toggle-price-filter :subsidy])}]]]

      [:div.filter-row
       {:data-ph-capture-attribute-ui_id (tracking/compose-ui-id "filterbar" "price" "preventive")}
       [:label {:class (when estg3no34-subsidizable-filter?
                         "active")}
        (t {:de "Nur Präventionsangebote"
            :en "Only prevention offer"})
        [:input {:type "checkbox"
                 :class "switch"
                 :name :subsidy
                 :checked (boolean estg3no34-subsidizable-filter?)
                 :on-change #(rf/dispatch [::toggle-price-filter :estg3no34-subsidizable?])}]]]

      [:div.filter-row
       [:label {:class (when mf-offer?
                         "active")}

        [:span.flex
         {:data-ph-capture-attribute-ui_id (tracking/compose-ui-id "filterbar" "price" "price-advantage")}
         (t {:de "Nur mit "
             :en "Only with "})
         [:img {:src (machtfit/static "img/machtfit-logo.png")
                :style {:height "1.2em"
                        :vertical-align "baseline"
                        :margin "0 0.5em"}}]
         (t {:de "-Preisvorteil"
             :en "-price advantage"})]
        [:input {:type "checkbox"
                 :class "switch"
                 :name :mf-offer
                 :checked (boolean mf-offer?)
                 :on-change #(rf/dispatch [::toggle-price-filter :mf-offer])}]]]

      [:div.filter-row
       {:data-ph-capture-attribute-ui_id (tracking/compose-ui-id "filterbar" "price" "free")}
       [:label {:class (when free-of-charge?
                         "active")}
        (t {:de "Nur kostenlose Angebote"
            :en "Only free offers"})
        [:input {:type "checkbox"
                 :class "switch"
                 :name :free-of-charge?
                 :checked (boolean free-of-charge?)
                 :on-change #(rf/dispatch [::toggle-price-filter :free-of-charge?])}]]]

      [:button.primary
       {:data-ph-capture-attribute-ui_id (tracking/compose-ui-id "filterbar" "price" "apply")
        :on-click (fn []
                    (rf/dispatch [::events/toggle-filter-dropdown :price])
                    (js/window.scrollTo 0 0))} (t {:de "Anwenden"
                                                   :en "Apply"})]]]))

(rf/reg-event-fx ::map-toggle-changed
  (fn [{:keys [db]} [_ ph-ui-id]]
    {::tracking/capture-event [db (if (:map-toggle? db) "map_closed" "map_opened") {:data-ph-capture-attribute-ui_id ph-ui-id}]
     :db (update db :map-toggle? not)}))

(defn map-toggle []
  (when-not @(rf/subscribe [:get [:user :machtfit-area-only-online?]])
    (let [ph-ui-id (tracking/compose-ui-id "map" "visibility-toggle")
          on-click #(do
                      (machtfit/stop-event %)
                      (rf/dispatch [::map-toggle-changed ph-ui-id]))]
      [:div.filter-map-toggle
       {:data-ph-capture-attribute-ui_id ph-ui-id
        :on-click on-click}
       [:span#map-toggle-label (t {:de "Karte anzeigen"
                                   :en "Show map"})]
       [switch/switch
        {:external-label-id "map-toggle-label"
         :checked? @(rf/subscribe [:get [:map-toggle?]])
         :on-click on-click}]])))

(defn sort-by-dropdown []
  (let [sort-by-open? (r/atom false)
        mouse-on-sort-by? (r/atom false)]
    (fn []
      (when-not @(rf/subscribe [:get [:map-toggle?]])
        (let [machtfit-area? (= @(rf/subscribe [::subs/effective-filter :area]) :machtfit)
              machtfit-area-only-online? @(rf/subscribe [:get [:user :machtfit-area-only-online?]])
              sort-by-options (cond-> [[:recommended (t {:de "Relevanz"
                                                         :en "relevance"})]
                                       [:price-asc (t {:de "Preis, aufsteigend"
                                                       :en "price, ascending"})]
                                       [:price-desc (t {:de "Preis, absteigend"
                                                        :en "price, descending"})]
                                       [:date-published (t {:de "Zuletzt hinzugefügt"
                                                            :en "recently added"})]]
                                (not (and machtfit-area?
                                          machtfit-area-only-online?)) (conj [:distance (t {:de "Entfernung"
                                                                                            :en "distance"})]))
              filter-sort-by @(rf/subscribe [::subs/effective-sort-by])]

          [:div.sort-by
           [:button.sort-by_button
            {:class (when @sort-by-open? "open")
             :on-click #(swap! sort-by-open? not)
             :on-blur #(when-not @mouse-on-sort-by?
                         (reset! sort-by-open? false))}
            [:span (t {:de "sortieren nach: "
                       :en "sort by: "})]
            (get (into {} sort-by-options) filter-sort-by)]
           (when @sort-by-open?
             [:div.popup
              {:on-mouse-enter #(reset! mouse-on-sort-by? true)
               :on-mouse-leave #(reset! mouse-on-sort-by? false)}
              [:span.sort-by_header "Sortieren nach:"]
              [:ul
               (for [[key translation] sort-by-options]
                 ^{:key key} [:li
                              {:data-ph-capture-attribute-ui_id (tracking/compose-ui-id "filterbar" "sort-by" key)}
                              [:button
                               {:class (when (= key filter-sort-by) "active")
                                :on-click #(do (reset! sort-by-open? false)
                                               (rf/dispatch [::events/sort-by-clicked key]))}
                               translation]])]])])))))

(defn- result-counter []
  (when (or @(rf/subscribe [:get [:user :machtfit-admin?]])
            @(rf/subscribe [:get [:user :offer-admin?]])
            @(rf/subscribe [:get [:user :impersonated?]]))
    [:div.result-counter
     (when @(rf/subscribe [::subs/products-loaded?])
       (let [show-map? @(rf/subscribe [::subs/show-map?])
             ;; The exact total, when the endpoint counted one, replaces the
             ;; local count and its bound. The flat catalogue counter has no
             ;; per-section arithmetic to keep consistent, so it can show the
             ;; real number.
             total (when-not show-map? @(rf/subscribe [::subs/result-count-total]))]
         [:div
          (str (cond
                 show-map? @(rf/subscribe [::subs/count-products-for-map])
                 (some? total) total
                 :else @(rf/subscribe [::subs/count-products-after-filter]))
               (when (and (nil? total) @(rf/subscribe [::subs/result-count-bound])) "+"))

          " " (t {:de "Angebote gefunden"
                  :en "offers found"})]))]))

(defn- relaxed-search-notice []
  [:div.flex.w-full.items-center.justify-center.mt-2.md:order-1.md:mt-0.md:w-auto.md:justify-start.md:whitespace-nowrap
   (translation/trp-with-place-holders
    {:de "Keine genauen Treffer für „{query}“. Wir zeigen dir ähnliche Angebote:"
     :en "No exact match for \"{query}\". Showing similar offers instead:"}
    {"query" @(rf/subscribe [::subs/effective-filter :keyword])})])

(defn- result-summary []
  (if @(rf/subscribe [::subs/relaxed-search?])
    [relaxed-search-notice]
    [result-counter]))

(defn filterbar []
  (let [open? (= @(rf/subscribe [:get [:ui :visible-mobile-dropdown]]) :mobile-filter)
        location-open? (= @(rf/subscribe [:get [:ui :visible-dropdown]]) :location)
        show-marketplace? @(rf/subscribe [::subs/show-marketplace?])
        has-company-area? @(rf/subscribe [:get [:user :has-company-area?]])]

    [:<>
     [:div.dropdown-fixed
      [:button
       {:class (when @(rf/subscribe [::subs/filter-set?]) "active")
        :on-click #(do
                     (machtfit/stop-event %)
                     (rf/dispatch [::events/toggle-filter-mobile-dropdown :mobile-filter]))}
       (str "Filter" (when @(rf/subscribe [::subs/filter-set?])
                       " (1+)"))]
      [:div.dropdown-menu
       {:class (when open?
                 "open")}
       [:header
        [:h5 "Filter"]
        [:button {:on-click #(rf/dispatch [::events/reset-filter-clicked])
                  :disabled (not @(rf/subscribe [::subs/filter-set?]))}
         (t {:de "zurücksetzen"
             :en "reset"})]]
       (when (and has-company-area? show-marketplace?)
         [areas])
       [product-types]
       [activities]
       (when-not (= @(rf/subscribe [::subs/effective-filter :area]) :company)
         [products])
       [price]
       [time-and-day]
       (when-not @(rf/subscribe [:get [:user :machtfit-area-only-online?]])
         [location @(rf/subscribe [:get [:products-location]]) location-open?])
       (when-not open?
         [map-toggle])
       [:button.primary
        {:on-click (fn []
                     (rf/dispatch [::events/toggle-filter-mobile-dropdown :mobile-filter])
                     (js/window.scrollTo 0 0))} (t {:de "Angebote anzeigen"
                                                    :en "Show offers"})]]]
     (when @(rf/subscribe [::subs/filter-set?])
       [filter-bubbles])

     [sort-by-dropdown]

     [result-summary]]))

